import React, { useState, useRef, useEffect, useContext } from 'react';
import {
  RACTextField,
  makeStyles,
  RACButton,
  Typography,
  InputProps,
} from '@rentacenter/racstrap';
import SecondFactor from '../History/TwoFactorPopup';
import { ContainerContext } from '../../../app/App';
import { CustomPropInterface } from '../../..';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
  addNoteContainer: {
    // padding: theme.spacing(0),
    paddingBottom: theme.spacing(1.5),
    boxSizing: 'border-box',
    width: '100%',
  },
  inputField: {
    marginBottom: theme.spacing(1),
    '& .MuiInputBase-root': {
      fontSize: '0.9rem',
      borderRadius: 12, // Add border radius
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 12, // Add border radius to outlined input
    },
    '& .MuiOutlinedInput-inputMultiline': {
      padding: theme.spacing(1.5),
      lineHeight: 1.5,
      minHeight: '5em', // Approximately 4-5 lines height
    },
    '& .MuiOutlinedInput-multiline': {
      padding: 0, // Remove extra padding since we're setting it on input
    },
    '& .MuiFormLabel-root': {
      fontSize: '0.9rem',
      transform: 'translate(14px, 16px) scale(1)',
    },
    '& .MuiFormLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
  },
  addButton: {
    marginTop: theme.spacing(0.5),
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(0.75, 0),
    borderRadius: 8, // Add border radius to button
  },
  title: {
    fontWeight: 600,
    fontSize: '0.95rem',
    marginBottom: theme.spacing(1),
  },
  characterCount: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textAlign: 'left',
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
  },
}));

interface AddNoteProps {
  onAddNote?: (noteText: string) => void;
  isSubmitting?: boolean;
}

/**
 * Component for adding new notes with improved paste and undo support
 */
export const AddNote: React.FC<AddNoteProps> = ({
  onAddNote,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const containerData = useContext(ContainerContext) as CustomPropInterface;
  const [noteText, setNoteText] = useState('');
  const [isOverLimit, setIsOverLimit] = useState(false);
  const [showSLAPopup, setShowSLAPopup] = useState(false);
  const [currentRole, setCurrentRole] = useState<string>('');
  const textFieldRef = useRef<HTMLTextAreaElement>(null);
  const MAX_NOTE_LENGTH = 1024;

  // Handle text field changes with improved paste handling
  const handleNoteTextChange = (value: string) => {
    const newValue = value;

    if (newValue.length <= MAX_NOTE_LENGTH) {
      setNoteText(newValue);
      setIsOverLimit(false);
    } else {
      // Only allow the maximum number of characters
      setNoteText(newValue.slice(0, MAX_NOTE_LENGTH));
      setIsOverLimit(true);
    }
  };

  // Handle paste events separately to preserve clipboard content formatting
  const handlePaste = () => {
    // Let the default paste happen but check length afterward
    setTimeout(() => {
      if (textFieldRef.current) {
        const pastedText = textFieldRef.current.value;
        if (pastedText.length > MAX_NOTE_LENGTH) {
          setNoteText(pastedText.slice(0, MAX_NOTE_LENGTH));
          setIsOverLimit(true);
        } else {
          setNoteText(pastedText);
          setIsOverLimit(false);
        }
      }
    }, 0);
  };

  // Handle undo and redo operations
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const isUndoRedo =
      ((e.ctrlKey || e.metaKey) && e.key === 'z') ||
      ((e.ctrlKey || e.metaKey) && e.key === 'y');

    if (isUndoRedo) {
      setTimeout(() => {
        if (textFieldRef.current) {
          const text = textFieldRef.current.value;
          const isValidLength = text.length <= MAX_NOTE_LENGTH;
          setNoteText(isValidLength ? text : text.slice(0, MAX_NOTE_LENGTH));
          setIsOverLimit(!isValidLength);
        }
      }, 0);
    }
  };

  // Handle button click with reduced complexity
  const handleAddNoteClick = () => {
    const trimmedText = noteText.trim();
    if (!trimmedText || trimmedText.length > MAX_NOTE_LENGTH) {
      return;
    }
    setShowSLAPopup(true);
  };

  // Handle SLA completion
  const handleSLACompleted = () => {
    setShowSLAPopup(false);
    onAddNote && onAddNote(noteText.trim());
    setNoteText('');
    setIsOverLimit(false);
  };

  // Handle SLA cancel
  const handleSLACancel = () => {
    setShowSLAPopup(false);
  };

  // Reset warning when text is fixed
  useEffect(() => {
    if (noteText.length <= MAX_NOTE_LENGTH && isOverLimit) {
      setIsOverLimit(false);
    }
  }, [noteText, isOverLimit]);

  // Get current role
  useEffect(() => {
    if (containerData) {
      const getCurrentRole = containerData.GetRole();
      setCurrentRole(getCurrentRole);
    }
  }, [containerData, t]);

  // Input props for the TextField
  const inputProps: Partial<InputProps> = {
    inputProps: {
      maxLength: MAX_NOTE_LENGTH,
    },
    inputRef: textFieldRef,
  };

  return (
    <div className={classes.addNoteContainer}>
      <Typography className={classes.title}>New Note</Typography>
      <RACTextField
        className={classes.inputField}
        multiline
        rows={4}
        variant="outlined"
        fullWidth
        value={noteText}
        onChange={handleNoteTextChange}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        disabled={isSubmitting}
        placeholder="Add a new note..."
        error={isOverLimit}
        InputProps={inputProps}
      />
      <Typography className={classes.characterCount}>
        {`(${MAX_NOTE_LENGTH}/${noteText.length})`}
      </Typography>
      <RACButton
        variant="contained"
        color="primary"
        className={classes.addButton}
        disabled={!noteText.trim() || isSubmitting || isOverLimit}
        fullWidth
        onClick={handleAddNoteClick}
      >
        {isSubmitting ? 'Adding...' : 'Add Note'}
      </RACButton>
      {showSLAPopup && (
        <SecondFactor
          setTwoFactorCompleted={handleSLACompleted}
          setTwoFactorCancelClick={handleSLACancel}
          CurrentRole={currentRole || 'RD'}
          moduleName="CustomerRetention"
        />
      )}
    </div>
  );
};
